import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";

import { toast } from "@/components/ui/use-toast";
import { useConfirmDialog } from "@/context";
import { useI18n } from "@/context/i18n";
import { formatCpfCnpj } from "@/core/utils";
import { queryClient } from "@/lib/api";

import * as insuranceCompaniesApi from "./insurance-companies.api";
import { IUseInsuranceCompanies } from "./insurance-companies.hooks.interface";
import {
  insuranceCompaniesFormSchema,
  TInsuranceCompaniesFormData,
} from "./insurance-companies.schema";
import { TInsuranceCompanies } from "./insurance-companies.type";

export const useInsuranceCompanies = (): IUseInsuranceCompanies => {
  const { t } = useI18n();
  const { openDialog } = useConfirmDialog();

  const [isInsuranceCompaniesFormOpen, setIsInsuranceCompaniesFormOpen] =
    React.useState<boolean>(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const openInsuranceCompaniesForm = React.useCallback(() => {
    setIsInsuranceCompaniesFormOpen(true);
  }, []);
  const closeInsuranceCompaniesForm = React.useCallback(() => {
    setIsInsuranceCompaniesFormOpen(false);
    setEditingId(null);
    insuranceCompaniesForm.reset({
      name: "",
      cnpj: "",
      corporate_name: "",
      type: "insurance",
    });
  }, []);

  const insuranceCompaniesForm = useForm<TInsuranceCompaniesFormData>({
    resolver: zodResolver(insuranceCompaniesFormSchema),
    defaultValues: {
      name: "",
      cnpj: "",
      corporate_name: "",
      type: "insurance",
    },
  });

  const { data: insuranceCompanies, error: insuranceCompaniesError } = useQuery(
    {
      queryKey: ["insuranceCompanies"],
      queryFn: insuranceCompaniesApi.getAllInsuranceCompanies,
    }
  );

  const { data: insuranceCompanyById } = useQuery({
    queryKey: ["insuranceCompanies", editingId],
    queryFn: () =>
      insuranceCompaniesApi.getInsuranceCompanyById(editingId || ""),
    enabled: Boolean(editingId),
  });

  const createInsuranceCompanyMutation = useMutation({
    mutationFn: insuranceCompaniesApi.createInsuranceCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insuranceCompanies"] });
      toast({
        title: t("generic.success"),
        description: t("insuranceCompanies.success.insuranceCompanyCreated"),
      });
      setIsInsuranceCompaniesFormOpen(false);
      insuranceCompaniesForm.reset();
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const createInsuranceCompany = React.useCallback(
    (data: TInsuranceCompaniesFormData) => {
      createInsuranceCompanyMutation.mutate(data);
    },
    [createInsuranceCompanyMutation]
  );

  const deleteInsuranceCompanyMutation = useMutation({
    mutationFn: insuranceCompaniesApi.deleteInsuranceCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insuranceCompanies"] });
      toast({
        title: t("generic.success"),
        description: t("insuranceCompanies.success.insuranceCompanyDeleted"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteInsuranceCompany = React.useCallback(
    (id: string) => {
      const insuranceCompany = insuranceCompanies?.find(
        (insuranceCompany) => insuranceCompany.id === id
      );
      openDialog(t("generic.confirmDelete"), insuranceCompany?.name || "", () =>
        deleteInsuranceCompanyMutation.mutate(id)
      );
    },
    [deleteInsuranceCompanyMutation, openDialog, t, insuranceCompanies]
  );

  const updateInsuranceCompanyMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: TInsuranceCompaniesFormData;
    }) =>
      insuranceCompaniesApi.updateInsuranceCompany(
        id,
        data as TInsuranceCompanies
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insuranceCompanies"] });
      if (editingId) {
        queryClient.invalidateQueries({
          queryKey: ["insuranceCompanies", editingId],
        });
      }
      toast({
        title: t("generic.success"),
        description: t("insuranceCompanies.success.insuranceCompanyUpdated"),
      });
      setIsInsuranceCompaniesFormOpen(false);
      setEditingId(null);
      insuranceCompaniesForm.reset({
        name: "",
        cnpj: "",
        corporate_name: "",
        type: "insurance",
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleInsuranceCompanyFormSubmit = React.useCallback(
    (values: TInsuranceCompaniesFormData) => {
      if (editingId) {
        updateInsuranceCompanyMutation.mutate({ id: editingId, data: values });
      } else {
        createInsuranceCompany(values);
      }
    },
    [createInsuranceCompany, editingId, updateInsuranceCompanyMutation]
  );

  React.useEffect(() => {
    if (insuranceCompaniesError) {
      toast({
        title: t("generic.error"),
        description: insuranceCompaniesError.message,
        variant: "destructive",
      });
    }
  }, [insuranceCompaniesError, t]);

  React.useEffect(() => {
    insuranceCompaniesForm.reset({
      name: "",
      cnpj: "",
      corporate_name: "",
      type: "insurance",
    });
  }, [insuranceCompaniesForm]);

  React.useEffect(() => {
    if (editingId && insuranceCompanyById) {
      insuranceCompaniesForm.reset({
        name: insuranceCompanyById.name || "",
        cnpj: formatCpfCnpj(insuranceCompanyById.cnpj || ""),
        corporate_name: insuranceCompanyById.corporate_name || "",
        type: insuranceCompanyById.type || "insurance",
      });
    }
  }, [editingId, insuranceCompanyById, insuranceCompaniesForm]);

  const editInsuranceCompany = React.useCallback((id: string) => {
    setEditingId(id);
    setIsInsuranceCompaniesFormOpen(true);
  }, []);

  return {
    isInsuranceCompaniesFormOpen,
    openInsuranceCompaniesForm,
    closeInsuranceCompaniesForm,
    insuranceCompaniesForm,
    insuranceCompanies: insuranceCompanies || [],
    handleInsuranceCompanyFormSubmit,
    deleteInsuranceCompany,
    editInsuranceCompany,
    editingId,
    insuranceCompanyById,
  };
};
